// @ts-nocheck
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { Loader2, Eye, Users, FileText, Calendar, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { parseLocalDate } from '@/utils/formatters';

interface NewsletterPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scheduleId: string;
  scheduleName: string;
  segmentName?: string;
  sport?: string;
}

interface Athlete {
  id: string;
  name: string;
  sport_name?: string;
  grad_year?: number;
  state?: string;
  city?: string;
  username?: string;
  avatar_url?: string;
}

interface Article {
  id: string;
  title: string;
  slug?: string;
  excerpt?: string;
  featured_image_url?: string;
  published_at?: string;
  sport?: string;
}

interface Camp {
  id: string;
  name: string;
  start_date?: string;
  end_date?: string;
  city?: string;
  state?: string;
  sport?: string;
}

interface Event {
  id: string;
  name: string;
  start_date?: string;
  city?: string;
  state?: string;
  sport?: string;
  slug?: string;
}

export default function NewsletterPreviewModal({
  open,
  onOpenChange,
  scheduleId,
  scheduleName,
  segmentName,
  sport,
}: NewsletterPreviewModalProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [camps, setCamps] = useState<Camp[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [segmentSport, setSegmentSport] = useState<string | null>(sport || null);

  useEffect(() => {
    if (open && scheduleId) {
      fetchPreviewContent();
    }
  }, [open, scheduleId]);

  const fetchPreviewContent = async () => {
    setIsLoading(true);
    try {
      // First get the schedule details to find the segment
      const { data: schedule } = await supabase
        .from('weekly_newsletter_schedules')
        .select('*')
        .eq('id', scheduleId)
        .single();

      let sportFilter = sport;

      // If we have an audience segment, get its sport filter
      if (schedule?.audience_segment_id) {
        const { data: segment } = await supabase
          .from('email_audience_segments')
          .select('*')
          .eq('id', schedule.audience_segment_id)
          .single();
        
        if (segment?.sport) {
          sportFilter = segment.sport;
        }
      }

      // If schedule has sport override, use that
      if (schedule?.sport) {
        sportFilter = schedule.sport;
      }

      setSegmentSport(sportFilter || null);

      // Fetch athletes
      let athletesQuery = supabase
        .from('profiles')
        .select('id, name, sport_name, grad_year, state, city, username, avatar_url')
        .eq('role', 'athlete')
        .eq('is_demo', false)
        .order('created_at', { ascending: false })
        .limit(6);

      if (sportFilter) {
        athletesQuery = athletesQuery.ilike('sport_name', `%${sportFilter}%`);
      }

      const { data: athletesData } = await athletesQuery;
      setAthletes(athletesData || []);

      // Articles no longer available (press_articles removed)
      setArticles([]);

      // Fetch camps
      let campsQuery = supabase
        .from('camps')
        .select('id, name, start_date, end_date, city, state, sport')
        .gte('start_date', new Date().toISOString().split('T')[0])
        .order('start_date', { ascending: true })
        .limit(4);

      if (sportFilter) {
        campsQuery = campsQuery.ilike('sport', `%${sportFilter}%`);
      }

      const { data: campsData } = await campsQuery;
      setCamps(campsData || []);

      // Fetch events
      let eventsQuery = supabase
        .from('events')
        .select('id, name, start_date, city, state, sport, slug')
        .gte('start_date', new Date().toISOString().split('T')[0])
        .order('start_date', { ascending: true })
        .limit(4);

      if (sportFilter) {
        eventsQuery = eventsQuery.ilike('sport', `%${sportFilter}%`);
      }

      const { data: eventsData } = await eventsQuery;
      setEvents(eventsData || []);

    } catch (error) {
      console.error('Error fetching preview content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Newsletter Content Preview: {scheduleName}
          </DialogTitle>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Segment: {segmentName || 'All'}</span>
            {segmentSport && (
              <span className="px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-medium">
                Sport: {segmentSport}
              </span>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className="h-[65vh]">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Tabs defaultValue="athletes" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="athletes" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Athletes ({athletes.length})
                </TabsTrigger>
                <TabsTrigger value="articles" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Articles ({articles.length})
                </TabsTrigger>
                <TabsTrigger value="camps" className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Camps ({camps.length})
                </TabsTrigger>
                <TabsTrigger value="events" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Events ({events.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="athletes">
                {athletes.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No athletes found{segmentSport ? ` for ${segmentSport}` : ''}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    {athletes.map((athlete) => (
                      <div key={athlete.id} className="border rounded-lg p-4 bg-card">
                        <div className="flex items-center gap-3 mb-2">
                          {athlete.avatar_url ? (
                            <img 
                              src={athlete.avatar_url} 
                              alt={athlete.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-primary font-semibold">
                                {athlete.name?.charAt(0) || '?'}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-sm">{athlete.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {athlete.sport_name} • {athlete.grad_year}
                            </p>
                          </div>
                        </div>
                        {(athlete.city || athlete.state) && (
                          <p className="text-xs text-muted-foreground">
                            {[athlete.city, athlete.state].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="articles">
                {articles.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No articles found{segmentSport ? ` for ${segmentSport}` : ''}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {articles.map((article) => (
                      <div key={article.id} className="border rounded-lg overflow-hidden bg-card">
                        {article.featured_image_url && (
                          <img 
                            src={article.featured_image_url}
                            alt={article.title}
                            className="w-full h-32 object-cover"
                          />
                        )}
                        <div className="p-4">
                          <p className="font-medium text-sm line-clamp-2">{article.title}</p>
                          {article.published_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {format(new Date(article.published_at), 'MMM d, yyyy')}
                            </p>
                          )}
                          {article.sport && (
                            <span className="inline-block mt-2 px-2 py-0.5 bg-secondary/10 text-secondary-foreground rounded text-xs">
                              {article.sport}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="camps">
                {camps.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No upcoming camps found{segmentSport ? ` for ${segmentSport}` : ''}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {camps.map((camp) => (
                      <div key={camp.id} className="border rounded-lg p-4 bg-card">
                        <p className="font-medium text-sm">{camp.name}</p>
                        {camp.start_date && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {format(parseLocalDate(camp.start_date), 'MMM d, yyyy')}
                            {camp.end_date && ` - ${format(parseLocalDate(camp.end_date), 'MMM d, yyyy')}`}
                          </p>
                        )}
                        {(camp.city || camp.state) && (
                          <p className="text-xs text-muted-foreground">
                            {[camp.city, camp.state].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {camp.sport && (
                          <span className="inline-block mt-2 px-2 py-0.5 bg-secondary/10 text-secondary-foreground rounded text-xs">
                            {camp.sport}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="events">
                {events.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No upcoming events found{segmentSport ? ` for ${segmentSport}` : ''}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    {events.map((event) => (
                      <div key={event.id} className="border rounded-lg p-4 bg-card">
                        <p className="font-medium text-sm">{event.name}</p>
                        {event.start_date && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {format(parseLocalDate(event.start_date), 'MMM d, yyyy')}
                          </p>
                        )}
                        {(event.city || event.state) && (
                          <p className="text-xs text-muted-foreground">
                            {[event.city, event.state].filter(Boolean).join(', ')}
                          </p>
                        )}
                        {event.sport && (
                          <span className="inline-block mt-2 px-2 py-0.5 bg-secondary/10 text-secondary-foreground rounded text-xs">
                            {event.sport}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </ScrollArea>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
          <p className="text-sm text-blue-800">
            <strong>Preview:</strong> This shows the content that will be included when this newsletter is sent.
            {segmentSport && ` Filtered to ${segmentSport} sport content.`}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}